import React from 'react';
import {
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    IconButton,
    ListItem,
    ListItemText,
    MenuItem,
    Paper,
    Popover,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import { FormattedMessage } from 'react-intl';
import { enUS, he } from 'date-fns/locale';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import config from 'config';
import ClearIcon from '@mui/icons-material/ClearOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { addDays, isValid, setMonth, setYear, isSameDay, startOfMonth as dfStartOfMonth, getDay, getDaysInMonth } from 'date-fns';
import { CalendarMonth, DateRange as DateRangeIcon } from '@mui/icons-material';
import {
    endOfMonth,
    endOfWeek,
    startOfMonth,
    startOfWeek,
    subtractMonths,
} from '../fromKotlin/nkutils';

const MONTHS_HE = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];
const DAY_NAMES = ['א׳','ב׳','ג׳','ד׳','ה׳','ו׳','ש׳']; // Sun-Sat

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 16 }, (_, i) => currentYear - 10 + i);

// Build calendar weeks for a given year/month (0-based month)
function buildCalendarWeeks(year: number, month: number): (Date | null)[][] {
    const firstDay = new Date(year, month, 1);
    const totalDays = getDaysInMonth(firstDay);
    // getDay(): 0=Sun ... 6=Sat  → we want Sun as first column
    const startOffset = getDay(firstDay); // 0-6
    const cells: (Date | null)[] = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks: (Date | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
}

interface MiniCalProps {
    label: string;
    viewYear: number;
    viewMonth: number;
    onViewChange: (year: number, month: number) => void;
    selectedDate: Date | null;
    onSelectDate: (d: Date) => void;
    minDate?: Date | null;
    highlightStart?: Date | null;
    highlightEnd?: Date | null;
    doubleClickHint?: boolean;
}

const MiniCal: React.FC<MiniCalProps> = ({
    label, viewYear, viewMonth, onViewChange,
    selectedDate, onSelectDate, minDate,
    highlightStart, highlightEnd, doubleClickHint,
}) => {
    const weeks = buildCalendarWeeks(viewYear, viewMonth);
    const today = new Date();

    const prevMonth = () => {
        if (viewMonth === 0) onViewChange(viewYear - 1, 11);
        else onViewChange(viewYear, viewMonth - 1);
    };
    const nextMonth = () => {
        if (viewMonth === 11) onViewChange(viewYear + 1, 0);
        else onViewChange(viewYear, viewMonth + 1);
    };

    const isDisabled = (d: Date) => !!minDate && d < minDate && !isSameDay(d, minDate);
    const isSelected = (d: Date) => !!selectedDate && isSameDay(d, selectedDate);
    const isInRange = (d: Date) => {
        if (!highlightStart || !highlightEnd) return false;
        const lo = highlightStart < highlightEnd ? highlightStart : highlightEnd;
        const hi = highlightStart < highlightEnd ? highlightEnd : highlightStart;
        return d >= lo && d <= hi;
    };
    const isToday = (d: Date) => isSameDay(d, today);

    return (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', userSelect: 'none' }}>
            {/* Header row: label + month select + year select + prev/next */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, pt: 1, pb: 0.5 }}>
                <IconButton size="small" onClick={prevMonth} sx={{ p: 0.3 }}><ChevronRightIcon fontSize="small" /></IconButton>
                <Select
                    size="small"
                    value={viewMonth}
                    onChange={(e) => onViewChange(viewYear, Number(e.target.value))}
                    sx={{ fontSize: 13, minWidth: 85, height: 26 }}
                >
                    {MONTHS_HE.map((m, i) => <MenuItem key={i} value={i} sx={{ fontSize: 13 }}>{m}</MenuItem>)}
                </Select>
                <Select
                    size="small"
                    value={viewYear}
                    onChange={(e) => onViewChange(Number(e.target.value), viewMonth)}
                    sx={{ fontSize: 13, minWidth: 68, height: 26 }}
                >
                    {YEARS.map((y) => <MenuItem key={y} value={y} sx={{ fontSize: 13 }}>{y}</MenuItem>)}
                </Select>
                <IconButton size="small" onClick={nextMonth} sx={{ p: 0.3 }}><ChevronLeftIcon fontSize="small" /></IconButton>
            </Box>
            <Typography variant="caption" sx={{ px: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontSize: 11 }}>
                {label}
            </Typography>

            {/* Day-name header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', px: 1, pb: 0.3 }}>
                {DAY_NAMES.map((n) => (
                    <Box key={n} sx={{ textAlign: 'center', fontSize: 11, color: 'text.disabled', fontWeight: 600, py: 0.3 }}>{n}</Box>
                ))}
            </Box>

            {/* Weeks */}
            <Box sx={{ px: 1, pb: 1 }}>
                {weeks.map((week, wi) => (
                    <Box key={wi} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                        {week.map((day, di) => {
                            if (!day) return <Box key={di} />;
                            const disabled = isDisabled(day);
                            const selected = isSelected(day);
                            const inRange = isInRange(day);
                            const todayMark = isToday(day);
                            return (
                                <Box
                                    key={di}
                                    onClick={() => !disabled && onSelectDate(day)}
                                    sx={{
                                        textAlign: 'center',
                                        py: 0.4,
                                        mx: 0.1,
                                        borderRadius: 1,
                                        fontSize: 13,
                                        cursor: disabled ? 'default' : 'pointer',
                                        fontWeight: todayMark ? 700 : 400,
                                        bgcolor: selected
                                            ? 'primary.main'
                                            : inRange
                                            ? 'primary.light'
                                            : 'transparent',
                                        color: selected
                                            ? 'primary.contrastText'
                                            : disabled
                                            ? 'text.disabled'
                                            : inRange
                                            ? 'primary.contrastText'
                                            : 'text.primary',
                                        outline: todayMark && !selected ? '1px solid' : 'none',
                                        outlineColor: 'primary.main',
                                        '&:hover': disabled ? {} : {
                                            bgcolor: selected ? 'primary.dark' : 'action.hover',
                                        },
                                        transition: 'background 0.15s',
                                    }}
                                >
                                    {day.getDate()}
                                </Box>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        </Paper>
    );
};

const MyRangePicker = ({ range, setRange, isLoading }) => {
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const [pickerOpen, setPickerOpen] = React.useState(false);
    const [pickerStart, setPickerStart] = React.useState<Date | null>(null);
    const [pickerEnd, setPickerEnd] = React.useState<Date | null>(null);
    const [openFastDatePicker, setOpenFastDatePicker] = React.useState(false);

    const [startViewYear, setStartViewYear] = React.useState(new Date().getFullYear());
    const [startViewMonth, setStartViewMonth] = React.useState(new Date().getMonth());
    const [endViewYear, setEndViewYear] = React.useState(new Date().getFullYear());
    const [endViewMonth, setEndViewMonth] = React.useState(new Date().getMonth());

    const lastStartClickRef = React.useRef<{ date: number; time: number } | null>(null);
    const lastEndClickRef = React.useRef<{ date: number; time: number } | null>(null);

    const openPicker = () => {
        const s: Date = range[0] ?? new Date();
        const e: Date = range[1] ?? new Date();
        setPickerStart(range[0] ?? null);
        setPickerEnd(range[1] ?? null);
        setStartViewYear(s.getFullYear());
        setStartViewMonth(s.getMonth());
        setEndViewYear(e.getFullYear());
        setEndViewMonth(e.getMonth());
        setPickerOpen(true);
    };

    const handleOk = (start?: Date | null, end?: Date | null) => {
        const s = start !== undefined ? start : pickerStart;
        const e = end !== undefined ? end : pickerEnd;
        if (s && e && isValid(s) && isValid(e)) setRange([s, e]);
        setPickerOpen(false);
    };

    const handleCancel = () => setPickerOpen(false);

    const applyFast = (start: Date, end: Date) => {
        setRange([start, end]);
        setOpenFastDatePicker(false);
    };

    const formatDate = (d: Date | null) => {
        if (!d || !isValid(d)) return '';
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    };

    const isOkDisabled = !pickerStart || !pickerEnd || !isValid(pickerStart) || !isValid(pickerEnd);

    const handleStartSelect = (day: Date) => {
        const now = Date.now();
        const t = day.getTime();
        if (
            lastStartClickRef.current &&
            lastStartClickRef.current.date === t &&
            now - lastStartClickRef.current.time < 600
        ) {
            // Double-click: use the clicked date as both start and end, auto-confirm
            lastStartClickRef.current = null;
            setPickerStart(day);
            handleOk(day, day);
            return;
        }
        lastStartClickRef.current = { date: t, time: now };
        setPickerStart(day);
        setStartViewYear(day.getFullYear());
        setStartViewMonth(day.getMonth());
    };

    const handleEndSelect = (day: Date) => {
        const now = Date.now();
        const t = day.getTime();
        if (
            lastEndClickRef.current &&
            lastEndClickRef.current.date === t &&
            now - lastEndClickRef.current.time < 600
        ) {
            // Double-click: use the clicked date as both start and end, auto-confirm
            lastEndClickRef.current = null;
            setPickerEnd(day);
            handleOk(day, day);
            return;
        }
        lastEndClickRef.current = { date: t, time: now };
        setPickerEnd(day);
        setEndViewYear(day.getFullYear());
        setEndViewMonth(day.getMonth());
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} ref={anchorRef}>

            <TextField
                size="small"
                label={<FormattedMessage id="From Date" />}
                value={formatDate(range[0])}
                onClick={!isLoading ? openPicker : undefined}
                inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
                disabled={isLoading}
                sx={{ width: 155 }}
            />
            <TextField
                size="small"
                label={<FormattedMessage id="To Date" />}
                value={formatDate(range[1])}
                onClick={!isLoading ? openPicker : undefined}
                inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
                disabled={isLoading}
                sx={{ width: 155 }}
            />
            <Tooltip title="בחר טווח תאריכים">
                <span>
                    <IconButton size="small" color="primary" disabled={isLoading} onClick={openPicker}>
                        <DateRangeIcon />
                    </IconButton>
                </span>
            </Tooltip>
            <Chip
                label="תאריך מהיר"
                onClick={() => setOpenFastDatePicker(true)}
                id="fastDatePicker"
                color="primary"
                variant="outlined"
                icon={<CalendarMonth />}
                disabled={isLoading}
            />

            {/* ── Range Picker Popover ── */}
            <Popover
                open={pickerOpen}
                anchorEl={anchorRef.current}
                onClose={handleCancel}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={config.i18n === 'he' ? he : enUS}>
                    <Box sx={{ p: 2 }}>
                        {/* Text inputs */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <DatePicker
                                        label={<FormattedMessage id="From Date" />}
                                        value={pickerStart}
                                        onChange={(val) => {
                                            setPickerStart(val);
                                            if (val && isValid(val)) {
                                                setStartViewYear(val.getFullYear());
                                                setStartViewMonth(val.getMonth());
                                            }
                                        }}
                                        renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                                        inputFormat="dd/MM/yyyy"
                                        mask="__/__/____"
                                        views={['year', 'month', 'day']}
                                        openTo="day"
                                    />
                                    {pickerStart && (
                                        <Tooltip title={<FormattedMessage id="clear" />}>
                                            <IconButton size="small" onClick={() => setPickerStart(null)}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <DatePicker
                                        label={<FormattedMessage id="To Date" />}
                                        value={pickerEnd}
                                        onChange={(val) => {
                                            setPickerEnd(val);
                                            if (val && isValid(val)) {
                                                setEndViewYear(val.getFullYear());
                                                setEndViewMonth(val.getMonth());
                                            }
                                        }}
                                        renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                                        inputFormat="dd/MM/yyyy"
                                        mask="__/__/____"
                                        views={['year', 'month', 'day']}
                                        openTo="day"
                                        minDate={pickerStart ?? undefined}
                                    />
                                    {pickerEnd && (
                                        <Tooltip title={<FormattedMessage id="clear" />}>
                                            <IconButton size="small" onClick={() => setPickerEnd(null)}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Two custom calendars */}
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                                <MiniCal
                                    label="מתאריך  •  לחץ פעמיים לאותו יום"
                                    viewYear={startViewYear}
                                    viewMonth={startViewMonth}
                                    onViewChange={(y, m) => { setStartViewYear(y); setStartViewMonth(m); }}
                                    selectedDate={pickerStart}
                                    onSelectDate={handleStartSelect}
                                    highlightStart={pickerStart}
                                    highlightEnd={pickerEnd}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MiniCal
                                    label="עד תאריך  •  לחץ פעמיים לאותו יום"
                                    viewYear={endViewYear}
                                    viewMonth={endViewMonth}
                                    onViewChange={(y, m) => { setEndViewYear(y); setEndViewMonth(m); }}
                                    selectedDate={pickerEnd}
                                    onSelectDate={handleEndSelect}
                                    minDate={pickerStart}
                                    highlightStart={pickerStart}
                                    highlightEnd={pickerEnd}
                                    doubleClickHint
                                />
                            </Grid>
                        </Grid>

                        {/* OK / Cancel */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                            <Button onClick={handleCancel} color="inherit" size="small">ביטול</Button>
                            <Button onClick={() => handleOk()} variant="contained" size="small" disabled={isOkDisabled}>אישור</Button>
                        </Box>
                    </Box>
                </LocalizationProvider>
            </Popover>

            {/* ── Fast Date Picker Popover ── */}
            <Popover
                open={openFastDatePicker}
                anchorEl={document.getElementById('fastDatePicker')}
                onClose={() => setOpenFastDatePicker(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <ListItem style={{ cursor: 'pointer' }} onClick={() => applyFast(addDays(new Date(), -1), addDays(new Date(), -1))}>
                    <ListItemText primary="אתמול" />
                </ListItem>
                <ListItem style={{ cursor: 'pointer' }} onClick={() => applyFast(new Date(), new Date())}>
                    <ListItemText primary="היום" />
                </ListItem>
                <ListItem style={{ cursor: 'pointer' }} onClick={() => applyFast(addDays(new Date(), 1), addDays(new Date(), 1))}>
                    <ListItemText primary="מחר" />
                </ListItem>
                <Divider />
                <ListItem style={{ cursor: 'pointer' }} onClick={() => applyFast(startOfWeek(new Date()), endOfWeek(new Date()))}>
                    <ListItemText primary="שבוע נוכחי" />
                </ListItem>
                <ListItem style={{ cursor: 'pointer' }} onClick={() => applyFast(startOfWeek(addDays(new Date(), -7)), endOfWeek(addDays(new Date(), -7)))}>
                    <ListItemText primary="שבוע קודם" />
                </ListItem>
                <Divider />

                <ListItem style={{ cursor: 'pointer' }} onClick={() => applyFast(startOfMonth(new Date()), endOfMonth(new Date()))}>
                    <ListItemText primary="חודש נוכחי" />
                </ListItem>
                <ListItem style={{ cursor: 'pointer' }} onClick={() => applyFast(subtractMonths(startOfMonth(new Date()), 1), endOfMonth(subtractMonths(new Date(), 1)))}>
                    <ListItemText primary="חודש קודם" />
                </ListItem>

                <Divider />

                <ListItem style={{ cursor: 'pointer' }} onClick={() => applyFast(addDays(new Date(), -7), new Date())}>
                    <ListItemText primary="שבוע אחורה" />
                </ListItem>
                <ListItem style={{ cursor: 'pointer' }} onClick={() => applyFast(addDays(new Date(), -14), new Date())}>
                    <ListItemText primary="שבועיים אחורה" />
                </ListItem>
                <ListItem style={{ cursor: 'pointer' }} onClick={() => applyFast(addDays(new Date(), -30), new Date())}>
                    <ListItemText primary="חודש אחורה" />
                </ListItem>

                <Divider />

                <ListItem style={{ cursor: 'pointer' }} onClick={() => {
                    const d = new Date();
                    applyFast(new Date(d.getFullYear(), 0, 1), d);
                }}>
                    <ListItemText primary="מתחילת שנה נוכחית" />
                </ListItem>
                <ListItem style={{ cursor: 'pointer' }} onClick={() => {
                    const year = new Date().getFullYear() - 1;
                    applyFast(new Date(year, 0, 1), new Date(year, 11, 31));
                }}>
                    <ListItemText primary="שנה קודמת" />
                </ListItem>
                <ListItem style={{ cursor: 'pointer' }} onClick={() => applyFast(addDays(new Date(), -365), new Date())}>
                    <ListItemText primary="שנה אחורה" />
                </ListItem>
                <ListItem style={{ cursor: 'pointer' }} onClick={() => applyFast(addDays(new Date(), -365 * 2), new Date())}>
                    <ListItemText primary="שנתיים אחורה" />
                </ListItem>
            </Popover>
        </Box>
    );
};

export default MyRangePicker;
